import React, {useEffect, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import DatePicker from "react-datepicker";
import {useRecoilState} from "recoil";
import Config from "../../core/config";
import {validateCPF} from "../../core/cpf";
import {searchCEP} from "../../core/Cep";
import {brazilianStates, formatLocalDate, parseLocalDate} from "../../utils/utils";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import BackButton from "../common/BackButton";import WhatsAppButton from "../../core/WhatsAppButton";
import {validateCNPJ} from "../../core/cnpj";
import {loadingAtom} from "../../atoms";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {UserPermission, CompanyPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";

const apiUrl = Config.apiUrl;

const CustomerManager = () => {
    const { hasUserPermission, hasCompanyPermission, loadingPermissions } = usePermission();
    const [, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');
    const [isCompany, setIsCompany] = useState(false);

    const newEntity = {
        id: undefined,
        name: '',
        address: '',
        addressNumber: '',
        addressComplement: '',
        neighborhood: '',
        cep: '',
        city: '',
        state: '',
        phone: '',
        email: '',
        instagram: '',
        birthDate: undefined,
        rg: '',
        cpfCnpj: '',
        delivery: false,
        spaceBorneDelivery: false,
        howDidYouFindUs: '',
        referral: '',
        note: '',
        highlightNote: false,
    };

    const newCustomerKid = {name: '', birthDate: undefined};

    const [formData, setFormData] = useState(newEntity);
    const [customerKids, setCustomerKids] = useState([newCustomerKid]);

    useEffect(() => {
        setIsCompany(isCompanyCode(formData.cpfCnpj));
    }, [formData.cpfCnpj]);

    function isCompanyCode(code) {
        const isCnpjValid = validateCNPJ(code);
        const numericCpfCnpj = code ? code.replace(/\D/g, '') : '';
        return isCnpjValid || numericCpfCnpj.length > 11;
    }

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleBirthDateChange = (date) => {
        setFormData((prevData) => ({
            ...prevData,
            birthDate: date ? formatLocalDate(date) : undefined
        }));
    };

    const handleKidBirthDateChange = (index, date) => {
        setCustomerKids((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    birthDate: date ? formatLocalDate(date) : undefined
                } : item
            )
        );
    };

    async function makeQuery() {
        if (id) {
            try {
                setLoading(true);
                const url = apiUrl + '/api/customers/id=' + id;
                const response = await makeRequest(url, 'GET');
                setFormData(response);
                setCustomerKids(response.kids);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        } else {
            setFormData(newEntity);
            setCustomerKids([newCustomerKid])
        }
    }

    useEffect(() => {
        makeQuery();
    }, [id]);

    useEffect(() => {
        if (customerKids.length === 0 || customerKids[customerKids.length - 1].name && customerKids[customerKids.length - 1].name !== '') {
            setCustomerKids([...customerKids, newCustomerKid]);
        }
    }, [customerKids]);

    function clearEntity() {
        setFormData(newEntity);
        setCustomerKids([newCustomerKid])
    }

    const onConfirm = async () => {
        try {
            if (validateInputs()) {
                setLoading(true);
                await new Promise(resolve => setTimeout(resolve, 300));
                await saveEntity();
                navigate('/app/customerlist');
            }
        } catch (error) {
            console.error('Error saving:', error);
            alert('Error ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    function validateInputs() {
        if (!formData.name || formData.name === '') {
            alert('Preencha o campo nome.');
            return false;
        }

        if (!formData.address || formData.address === '') {
            alert('Preencha o campo endereço.');
            return false;
        }

        if (!formData.neighborhood || formData.neighborhood === '') {
            alert('Preencha o campo bairro.');
            return false;
        }

        if (!formData.cep || formData.cep === '') {
            alert('Preencha o campo cep.');
            return false;
        }

        if (!formData.city || formData.city === '') {
            alert('Preencha o campo cidade.');
            return false;
        }

        if (!formData.state || formData.state === '') {
            alert('Preencha o campo estado.');
            return false;
        }

        if (!formData.phone || formData.phone === '') {
            alert('Preencha o campo telefone.');
            return false;
        }

        if(!isCompany) {
            if (!formData.birthDate || formData.birthDate === '') {
                alert('Preencha o campo data de nascimento.');
                return false;
            }
        }

        if (!isCompany) {
            if (!validateCPF(formData.cpfCnpj)) {
                alert('CPF inválido.');
                return false;
            }
        } else {
            if (!validateCNPJ(formData.cpfCnpj)) {
                alert('CNPJ inválido.');
                return false;
            }
        }
        if (!formData.cpfCnpj || formData.cpfCnpj === '') {
            alert('Preencha o campo CPF/CNPJ.');
            return false;
        }

        return true;
    }

    async function saveEntity() {
        formData.kids = customerKids.filter((p) => p.name && p.name !== '');
        const url = apiUrl + '/api/customers';
        await makeRequest(url, id ? 'PUT' : 'POST', {
            'Content-Type': 'application/json',
        }, JSON.stringify(formData));
    }

    async function searchCepAndFillAddressInputs() {
        let result = undefined;
        setLoading(true);
        try {
            result = await searchCEP(formData.cep);
        } finally {
            setLoading(false);
        }
        if (result != null) {
            setFormData((prevData) => ({
                ...prevData,
                address: result.street,
                neighborhood: result.neighborhood,
                city: result.city,
                state: result.state,
            }));
        } else {
            alert('CEP não encontrado.');
        }
    }

    function handleKidNameChange(index, name) {
        setCustomerKids((prevItems) =>
            prevItems.map((item, i) =>
                i === index ? {
                    ...item,
                    name:
                    name
                } : item
            )
        );
    }

    function removeCustomerKid(index) {
        const novaLista = [...customerKids];
        novaLista.splice(index, 1);
        setCustomerKids(novaLista);
    }

    function handleStateChange(idState) {
        setFormData((prevData) => ({
            ...prevData,
            state: idState,
        }));
    }

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (hasUserPermission(UserPermission.CREATE_CUSTOMER) === false || (hasUserPermission(UserPermission.EDIT_CUSTOMER) === false && id)) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <div>
            <div className="header-label">{formData.id > 0 ? 'Editar' : 'Cadastrar'} Cliente</div>

            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Nome:</div>
                    <input type="text" style={{width: '300px'}} value={formData.name} name="name"
                           onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>CPF/CNPJ:</div>
                    <input type="text" id="cpfCnpj" value={formData.cpfCnpj} name="cpfCnpj" maxLength="18"
                           onChange={handleChange}/>
                </div>

                {!isCompany &&
                    <>
                        <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                            <div style={{width: '180px'}}>RG:</div>
                            <input type="text" id="rg" value={formData.rg} name="rg" onChange={handleChange}/>
                        </div>

                        <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                            <div style={{width: '180px'}}>Data de Nascimento:</div>
                            <DatePicker
                                name="birthDate"
                                selected={formData.birthDate ? parseLocalDate(formData.birthDate) : undefined}
                                onChange={handleBirthDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                        </div>
                    </>
                }
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '80px'}}>CEP:</div>
                    <div>
                        <input style={{width: '80px'}} type="text" id="cep" value={formData.cep} name="cep"
                               onChange={handleChange}/>
                        <button onClick={searchCepAndFillAddressInputs}>Buscar CEP</button>
                    </div>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Endereço:</div>
                    <input type="text" style={{width: '297px'}} value={formData.address} name="address"
                           onChange={handleChange} autoComplete={false}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '70px'}}>Número:</div>
                    <input type="number" step="1" placeholder="0" style={{width: '70px'}}
                           value={formData.addressNumber} name="addressNumber"
                           onChange={handleChange} autoComplete={false}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Complemento:</div>
                    <input type="text" style={{width: '310px'}} value={formData.addressComplement}
                           name="addressComplement"
                           onChange={handleChange} autoComplete={false}/>
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Bairro:</div>
                    <input type="text" style={{width: '400px'}} id="neighborhood" value={formData.neighborhood}
                           name="neighborhood" onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Cidade:</div>
                    <input type="text" style={{width: '290px'}} id="city" value={formData.city}
                           name="city" onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Estado:</div>
                    <select name='state' value={formData.state} onChange={(e) => handleStateChange(e.target.value)}>
                        <option value="">Selecione...</option>
                        {brazilianStates.map((estado) => (
                            <option key={estado.sigla} value={estado.sigla}>
                                {estado.sigla} - {estado.nome}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '150px'}}>Telefone:</div>
                    <div style={{display: 'flex'}}>
                        <input type="text" id="phone" value={formData.phone} name="phone" onChange={handleChange}/>
                        <WhatsAppButton phoneNumber={formData.phone}/>
                    </div>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Email:</div>
                    <input type="text" style={{width: '300px'}} id="email" value={formData.email} name="email"
                           onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '190px'}}>Instagram:</div>
                    <input style={{width: '190px'}} type="text" id="instagram" value={formData.instagram}
                           name="instagram"
                           onChange={handleChange}/>
                </div>
            </div>

            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center', display: 'flex'}}>
                    <div>Entrega?</div>
                    <input type="checkbox" id="delivery" checked={formData.delivery} name="delivery"
                           onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '10px', alignItems: 'center', display: 'flex'}}>
                    <div>Entrega com Astronauta?</div>
                    <input type="checkbox" id="spaceBorneDelivery" checked={formData.spaceBorneDelivery}
                           name="spaceBorneDelivery" onChange={handleChange} disabled={!hasCompanyPermission(CompanyPermission.SPACE_BORNE_DELIVERY)}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '10px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Como nos encontrou:</div>
                    <input type="text" style={{width: '400px'}} id="howDidYouFindUs"
                           value={formData.howDidYouFindUs}
                           name="howDidYouFindUs" onChange={handleChange}/>
                </div>

                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Indicação:</div>
                    <input type="text" id="referral" value={formData.referral} name="referral"
                           onChange={handleChange}/>
                </div>
            </div>

            <div style={{display: 'flex'}}>
                <div style={{marginTop: '5px', alignItems: 'center'}}>
                    <div style={{width: '180px'}}>Observação:</div>
                    <input type='text' style={{width: '600px'}} id="note" value={formData.note}
                           name="note" onChange={handleChange}/>
                </div>
                <div style={{marginTop: '5px', marginLeft: '5px', alignItems: 'center', display: 'flex'}}>
                    <div>Destacar observação?</div>
                    <input type="checkbox" id="highlightNote" checked={formData.highlightNote}
                           name="highlightNote" onChange={handleChange}/>
                </div>
            </div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Astrolovers:</div>

                <table style={{height: '125px'}}>
                    <thead>
                    <tr>
                        <th>Nome do Astrolover</th>
                        <th>Data de Nascimento</th>
                        <th/>
                    </tr>
                    </thead>
                    <tbody style={{overflowY: 'auto'}}>
                    {customerKids.map((item, index) => (
                        <tr>
                            <td>
                                <input type="text"
                                       name={"kidName"+index}
                                       value={item.name}
                                       onChange={(e) => handleKidNameChange(index, e.target.value)}/>
                            </td>
                            <td>
                                <DatePicker
                                    name={"kidBirthDate"+index}
                                    selected={item.birthDate ? parseLocalDate(item.birthDate) : undefined}
                                    onChange={(date) => handleKidBirthDateChange(index, date)}
                                    dateFormat="dd/MM/yyyy"
                                    isClearable
                                    autoComplete="off"
                                />
                            </td>
                            <td>
                                {index < customerKids.length - 1 && (
                                    <button className="remove-button"
                                            onClick={() => removeCustomerKid(index)}>Excluir</button>)}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <ActionButtonsManagerView>
                <BackButton />
                <ConfirmButton onClick={onConfirm}/>
            </ActionButtonsManagerView>
        </div>
    );
};

export default CustomerManager;
